import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import usePopover from "components/common/usePopover";
import { loadReferralSpecialties } from "../helper/referralSpecialties";

/**
 * The referral's Specialty — a searchable dropdown over the service names of
 * the four referable consultation subtypes (product, 2026-09-10). Options come
 * from helper/referralSpecialties, which reuses the service-dashboard filter.
 *
 * SEARCHABLE, BUT NOT FREE TEXT. Typing FILTERS the list; it does not set the
 * specialty. Only picking a row does.
 *
 * This reverses the original call here, and the reversal is product's: a typed
 * specialty was allowed so a clinician could refer to something Zyla does not
 * itself sell, and what it actually produced was invalid referrals going out on
 * signed prescriptions (Nittin, 2026-09-12: "Invalid Referral Speciality can
 * also be added and sent in the Prescription. We should not allow custom
 * speciality referrals"). So the field is now a hard select with a search box
 * in front of it.
 *
 * The consequence to know about: if the catalogue read fails, NO referral can
 * be added at all — there is nothing to pick. The empty state says so instead
 * of inviting text that will not be kept.
 *
 * Portalled through usePopover for the reason every overlay in this repo is:
 * the pinned Rx pane is `overflow-y: auto` above 1181px, and an absolutely
 * positioned menu inside it gets clipped by that ancestor.
 */
const MAX_ROWS = 60;

const Chevron = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M4 6.5l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SpecialtyPicker = ({ id, value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(null); // null = not loaded yet
  const [query, setQuery] = useState("");
  /* What the input shows: the query while the menu is open, the committed value
     when it is closed. Without that split, typing to search would overwrite the
     field and closing the menu would leave a half-typed specialty behind. */
  const typing = useRef(false);

  const shown = useMemo(() => {
    const rows = options || [];
    const q = query.trim().toLowerCase();
    const hits = q ? rows.filter((r) => r.label.toLowerCase().indexOf(q) >= 0) : rows;
    return hits.slice(0, MAX_ROWS);
  }, [options, query]);

  /* Dismissal drops the half-typed search as well as closing the menu, so the
     next open starts from the full list rather than from whatever was typed
     and abandoned. */
  const dismiss = () => {
    setOpen(false);
    setQuery("");
    typing.current = false;
  };
  /* reflowKey: this menu's height changes UNDER ITS OWN placement — it opens
     as a one-line "Loading specialties..." box, becomes a long list when the
     catalogue lands, and shrinks again as the search narrows. Without it the
     position is measured once, and a list filtered down to one row keeps the
     top it was given as a fifteen-row list, leaving the row floating above the
     field (see the note on usePopover's parameter). */
  const { triggerRef, menuRef, menuStyle } = usePopover(
    open,
    dismiss,
    options === null ? "loading" : shown.length
  );

  useEffect(() => {
    if (!open || options !== null) return;
    let cancelled = false;
    loadReferralSpecialties().then((rows) => {
      if (!cancelled) setOptions(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [open, options]);

  const commit = (label) => {
    onChange(label);
    setQuery("");
    typing.current = false;
    setOpen(false);
  };

  return (
    <>
      <span className="rx-spec-field" ref={triggerRef}>
        <input
          id={id}
          type="text"
          className="rx-input"
          /* The committed value once closed, so the field always reads back
             what will print. */
          value={open && typing.current ? query : value}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-menu`}
          /* CLICK, not focus. usePopover dismisses on any scroll outside the
             menu (usePopover.js:82-90), and focusing a field that is not fully
             in view makes the browser scroll it there — so opening on focus
             opened the list and closed it in the same tick, which is what the
             first harness run caught. A click means the field was already
             visible. Tabbing through the form no longer pops the list open
             either, which is the better behaviour anyway. */
          onClick={() => setOpen(true)}
          onChange={(e) => {
            typing.current = true;
            /* SEARCH ONLY. The typed text never reaches the form — the
               specialty changes in `commit` and nowhere else, which is what
               makes an off-catalogue referral impossible rather than merely
               discouraged. Closing the field reverts it to the committed
               value (see `value` above). */
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape" && open) {
              e.stopPropagation();
              dismiss();
            }
            if (e.key === "Enter" && open && shown.length === 1) {
              e.preventDefault();
              commit(shown[0].label);
            }
          }}
        />
        <button
          type="button"
          className={`rx-spec-toggle${open ? " is-open" : ""}`}
          aria-label={open ? "Hide specialties" : "Show specialties"}
          tabIndex={-1}
          onClick={() => setOpen((o) => !o)}
        >
          <Chevron />
        </button>
      </span>

      {open &&
        ReactDOM.createPortal(
          <div
            id={`${id}-menu`}
            className="rx-spec-menu"
            ref={menuRef}
            style={menuStyle}
            role="listbox"
            aria-label="Specialties"
          >
            {options === null && <p className="rx-spec-empty">Loading specialties&hellip;</p>}
            {options !== null && options.length === 0 && (
              /* The catalogue read failed or matched nothing. This BLOCKS the
                 referral now, so the line says what is wrong rather than
                 inviting text that would be discarded. */
              <p className="rx-spec-empty">
                No specialties available &mdash; reload the page to try again.
              </p>
            )}
            {options !== null && options.length > 0 && shown.length === 0 && (
              <p className="rx-spec-empty">
                No specialty matches &ldquo;{query.trim()}&rdquo;.
              </p>
            )}
            {shown.map((r) => (
              <button
                type="button"
                key={r.label}
                className={`rx-spec-row${r.label === value ? " is-on" : ""}`}
                role="option"
                aria-selected={r.label === value}
                onClick={() => commit(r.label)}
              >
                <span className="rx-spec-name">{r.label}</span>
                {/* Which of the four it came from — two services can read alike
                    ("Consultation") and the subtype is what separates them. */}
                {r.subtype ? <span className="rx-spec-sub">{r.subtype}</span> : null}
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

SpecialtyPicker.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

export default SpecialtyPicker;
